'use client';
import { useState, useRef, useCallback, useEffect } from 'react';
import { useTranslations as useTranslations$1 } from 'next-intl';
import { usePathname } from 'next/navigation';
import { HelpCircle, X, Check, Loader2, Paperclip } from '@/components/ui/icons';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { useAuth } from '@/hooks/useAuth';
import { submitFeedback, uploadFrame } from '@/lib/api';
import { captureSentryError } from '@/lib/sentry';
import { isImageFile, readImageFileAsDataUrl } from '@/lib/uploadImage';
interface Attachment {
    id: string;
    dataUrl: string;
    label: string;
}
const MAX_ATTACHMENTS = 3;
export function defaultSupportCategory(pathname: string | null): 'Question' | 'Feature request' {
    return pathname?.startsWith('/welcome') ? 'Question' : 'Feature request';
}
export function SupportForm({ triggerVariant = 'default' }: {
    triggerVariant?: 'default' | 'menu-item';
}) {
    const t = useTranslations$1();
    const pathname = usePathname();
    const defaultCategory = defaultSupportCategory(pathname);
    const copy = {
        trigger: t("HqRNN8"),
        sent: t("gcuDcH"),
        bug: t("spnNJe"),
        featureRequest: t("eVIjIQ"),
        question: t("kgOBET"),
        placeholder: t("U29Jof"),
        attachImage: t("HkX8Ul"),
        replyTo: (email: string)=>t("kLYKhq", {
                email
            }),
        send: t("9WRlF4"),
        failedRead: t("TSCdRu"),
        genericError: t("3kJE8G")
    };
    const { email } = useAuth();
    const [open, setOpen] = useState(false);
    const [message, setMessage] = useState('');
    const [category, setCategory] = useState<string>(defaultCategory);
    const [attachments, setAttachments] = useState<Attachment[]>([]);
    const [submitting, setSubmitting] = useState(false);
    const [sent, setSent] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const textareaRef = useRef<HTMLTextAreaElement>(null);
    const fileInputRef = useRef<HTMLInputElement>(null);
    useEffect(()=>{
        if (!open) return;
        const timeoutId = window.setTimeout(()=>textareaRef.current?.focus(), 100);
        return ()=>window.clearTimeout(timeoutId);
    }, [
        open
    ]);
    const handleOpenChange = useCallback((nextOpen: boolean)=>{
        setOpen(nextOpen);
        if (nextOpen) {
            setCategory(defaultCategory);
            return;
        }
        setMessage('');
        setCategory(defaultCategory);
        setAttachments([]);
        setSubmitting(false);
        setSent(false);
        setError(null);
    }, [
        defaultCategory
    ]);
    const handleFileSelect = useCallback(async (e: React.ChangeEvent<HTMLInputElement>)=>{
        const file = e.target.files?.[0];
        if (!file) return;
        if (!isImageFile(file)) {
            e.target.value = '';
            return;
        }
        try {
            const dataUrl = await readImageFileAsDataUrl(file);
            setAttachments((prev)=>{
                if (prev.length >= MAX_ATTACHMENTS) return prev;
                return [
                    ...prev,
                    {
                        id: crypto.randomUUID(),
                        dataUrl,
                        label: file.name
                    }
                ];
            });
        } catch (err) {
            captureSentryError(err instanceof Error ? err : new Error(String(err)), {
                operation: 'support_form_attachment_normalize',
                component: 'SupportForm',
                extra: {
                    fileName: file.name
                }
            });
            setError(copy.failedRead);
        } finally{
            e.target.value = '';
        }
    }, [
        copy.failedRead
    ]);
    const removeAttachment = useCallback((id: string)=>{
        setAttachments((prev)=>prev.filter((a)=>a.id !== id));
    }, []);
    const handleSubmit = async ()=>{
        if (!message.trim() || submitting) return;
        setSubmitting(true);
        setError(null);
        try {
            const uploadResults = await Promise.all(attachments.map(async (a)=>{
                try {
                    const result = await uploadFrame(a.dataUrl);
                    return result.url;
                } catch (uploadErr) {
                    captureSentryError(uploadErr instanceof Error ? uploadErr : new Error(String(uploadErr)), {
                        operation: 'support_form_attachment_upload',
                        component: 'SupportForm',
                        extra: {
                            fileName: a.label
                        }
                    });
                    return null;
                }
            }));
            const screenshotUrl = uploadResults.filter(Boolean).join('\n') || undefined;
            await submitFeedback({
                message,
                category,
                priority: 'Medium',
                screenshotUrl,
                pageUrl: window.location.href,
                userAgent: navigator.userAgent
            });
            setSent(true);
            setTimeout(()=>handleOpenChange(false), 1500);
        } catch (err) {
            setError(copy.genericError);
            captureSentryError(err instanceof Error ? err : new Error(String(err)), {
                operation: 'submit_feedback'
            });
        } finally{
            setSubmitting(false);
        }
    };
    return (<Popover open={open} onOpenChange={handleOpenChange} modal={false}>
      <PopoverTrigger asChild>
        <Button variant="ghost" size="sm" className={triggerVariant === 'menu-item' ? 'h-11 w-full justify-start gap-3 px-3 text-muted-foreground hover:text-foreground' : 'gap-1.5 text-muted-foreground hover:text-foreground'}>
          <HelpCircle className="w-4 h-4"/>
          <span className="text-sm">{copy.trigger}</span>
        </Button>
      </PopoverTrigger>
      <PopoverContent align="end" side="bottom" sideOffset={16} className="w-96 p-0 overflow-hidden bg-card">
        {sent ? (<div className="flex items-center justify-center gap-2 py-8">
            <Check className="w-4 h-4 text-muted-foreground"/>
            <span className="text-sm text-muted-foreground">{copy.sent}</span>
          </div>) : (<>
            { /* Category */ }
            <div className="px-4 pt-3 pb-1">
              <Select value={category} onValueChange={setCategory}>
                <SelectTrigger className="h-7 w-auto inline-flex text-[11px] gap-1 pe-2">
                  <SelectValue/>
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Bug" className="text-xs">
                    {copy.bug}
                  </SelectItem>
                  <SelectItem value="Feature request" className="text-xs">
                    {copy.featureRequest}
                  </SelectItem>
                  <SelectItem value="Question" className="text-xs">
                    {copy.question}
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>

            { /* Message */ }
            <div className="px-4 py-2">
              <Textarea ref={textareaRef} placeholder={copy.placeholder} value={message} onChange={(e)=>setMessage(e.target.value)} rows={5} className="resize-none text-xs bg-background"/>
            </div>

            { /* Attachments + actions */ }
            <div className="px-4 pb-2 flex items-center gap-2 flex-wrap">
              {attachments.map((a)=>(<div key={a.id} className="relative group shrink-0">
                  <img src={a.dataUrl} alt={a.label} className="h-10 w-auto rounded border border-border object-cover"/>
                  <button type="button" onClick={()=>removeAttachment(a.id)} className="absolute -top-1.5 -end-1.5 flex h-4 w-4 items-center justify-center rounded-full bg-secondary text-muted-foreground hover:text-foreground hover:bg-muted transition-colors opacity-0 group-hover:opacity-100">
                    <X className="w-2.5 h-2.5"/>
                  </button>
                </div>))}

              <input ref={fileInputRef} type="file" accept="image/*" onChange={handleFileSelect} className="hidden"/>

              {attachments.length < MAX_ATTACHMENTS && (<button type="button" onClick={()=>fileInputRef.current?.click()} className="flex h-7 w-7 items-center justify-center rounded text-muted-foreground/40 hover:text-muted-foreground hover:bg-secondary transition-colors" title={copy.attachImage}>
                  <Paperclip className="w-3.5 h-3.5"/>
                </button>)}
            </div>

            { /* Footer */ }
            <div className="flex items-center border-t border-border px-4 py-2.5 gap-3">
              {error ? (<span className="text-[11px] text-destructive truncate">{error}</span>) : email ? (<span className="text-[10px] text-muted-foreground/50 truncate">{copy.replyTo(email)}</span>) : null}
              <Button size="sm" onClick={handleSubmit} disabled={!message.trim() || submitting} className="ms-auto h-7 px-3 text-xs shrink-0">
                {submitting ? <Loader2 className="w-3.5 h-3.5 animate-spin"/> : copy.send}
              </Button>
            </div>
          </>)}
      </PopoverContent>
    </Popover>);
}
